import { useState, useEffect } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import s from './GenerateProposalModal.scss'
import { SelectTemplate } from './SelectTemplate'
import { makeGetRequest, makePostRequest } from 'utils/api'
import { useLoading } from 'hooks'
import { sortAlphabetically } from 'utils/helpers'
import { toTemplates } from 'utils/serializers'
import { DetailsForm } from './DetailsForm'
import { useImmer } from 'use-immer'
import { EditTemplates } from './EditTemplates'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { useNavigate } from 'react-router-dom'
import { toDeal } from './serializers'

const GenerateProposalModal = ({
  isGenerateProposalModalVisible,
  setIsGenerateProposalVisible,
  setIsProgressModalVisible,
  setLoadingStep
}) => {
  const [step, setStep] = useState(1)
  const [selectedTemplate, setSelectedTemplate] = useImmer(null)
  const [userHtmlTemplates, setUserHtmlTemplates] = useState([])
  const [userWordTemplates, setUserWordTemplates] = useState([])
  const [iDealHtmlTemplates, setIDealHtmlTemplates] = useState([])
  const [iDealWordTemplates, setIDealWordTemplates] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [selectedDeal, setSelectedDeal] = useImmer(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/proposaltemplates/html'))
      setUserHtmlTemplates(
        sortAlphabetically(toTemplates(result), 'name').map(t => ({
          ...t,
          value: t.id,
          label: t.name
        }))
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/proposaltemplates'))
      setUserWordTemplates(
        sortAlphabetically(result, 'title').map(t => ({
          ...t,
          value: t.id,
          label: t.title
        }))
      )
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/predesignedproposaltemplates/html'))
      setIDealHtmlTemplates(result)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    ;(async () => {
      const result = await withLoadingLocks(makeGetRequest('/v1/predesignedproposaltemplates'))
      setIDealWordTemplates(result)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeModal = () => {
    setIsGenerateProposalVisible(false)
  }

  const isValidDeal = () => {
    if (!selectedDeal) return false
    if (selectedDeal?.type === 'existing') {
      return true
    }
    return (
      selectedDeal?.value.name &&
      selectedDeal?.value.address &&
      selectedDeal?.value.city &&
      selectedDeal?.value.state &&
      selectedDeal?.value.clients?.length
    )
  }

  const createProposal = async (body, base64body, pageType) => {
    let deal = null
    let template = null
    closeModal()
    setIsProgressModalVisible(true)

    // create deal
    if (selectedDeal.type === 'new') {
      try {
        setLoadingStep('Creating Opportunity...')
        deal = await makePostRequest('/v1/deals', toDeal(selectedDeal.value))
      } catch (e) {
        setLoadingStep('')
        setIsProgressModalVisible(false)
        dispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem creating deal',
            level: 'error'
          }
        })
      }
    }

    // install predesigned template
    if (selectedTemplate.subtype === 'ideal') {
      try {
        setLoadingStep('Installing Template...')
        template = await makePostRequest('/v1/proposaltemplates/presignedtemplate', {
          id: selectedTemplate.value.id,
          thumbnailUrl: selectedTemplate.value.thumbnailUrl
        })
      } catch (e) {
        setLoadingStep('')
        setIsProgressModalVisible(false)
        dispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem installing template',
            level: 'error'
          }
        })
      }
    }

    // create proposal
    try {
      setLoadingStep('Creating Proposal...')
      await makePostRequest(
        `/v1/deals/${
          selectedDeal.type === 'new' ? deal.id : selectedDeal.value.value
        }/generateproposals/${
          selectedTemplate.subtype === 'ideal' ? template.id : selectedTemplate.value.id
        }`,
        {
          baseBid: 1,
          pageType: pageType.toUpperCase() ?? 'A4',
          proposals: selectedDeal.value.clients.map(c => ({
            companyId: selectedDeal.type === 'new' ? c.value : c.company.id,
            proposalId: 0
          })),
          template: base64body
        }
      )
      setIsProgressModalVisible(false)
      navigate(
        `/opportunities/${
          selectedDeal.type === 'new' ? deal.id : selectedDeal.value.value
        }/proposals`
      )
    } catch (e) {
      setLoadingStep('')
      setIsProgressModalVisible(false)
      dispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was a problem creating proposal',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <Modal
        keyboard={false}
        backdrop="static"
        isOpen={isGenerateProposalModalVisible}
        toggle={closeModal}
        size="xl"
        className={s.modal}
      >
        <ModalHeader toggle={closeModal} className={s.modalHeader}>
          Generate Proposal
        </ModalHeader>
        <ModalBody className={s.modalBody}>
          {step === 1 && (
            <SelectTemplate
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              userHtmlTemplates={userHtmlTemplates}
              iDealHtmlTemplates={iDealHtmlTemplates}
              iDealWordTemplates={iDealWordTemplates}
              userWordTemplates={userWordTemplates}
            />
          )}
          {step === 2 && (
            <DetailsForm setSelectedDeal={setSelectedDeal} selectedDeal={selectedDeal} />
          )}
          {step === 3 && (
            <EditTemplates selectedTemplate={selectedTemplate} createProposal={createProposal} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          {step > 1 && (
            <Button color="secondary" onClick={() => setStep(step => step - 1)}>
              Back
            </Button>
          )}
          {step !== 3 && (
            <Button
              disabled={(step === 1 && !selectedTemplate) || (step === 2 && !isValidDeal())}
              className={s.saveBtn}
              onClick={() => setStep(step => step + 1)}
            >
              Next
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  )
}

export default GenerateProposalModal
